import { GetIssueRelationshipsResponseDto } from '@issuemanager/api-client';
import { useLocalizedRelationshipTypes } from '@issuemanager/client/features/issues';
import {
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { RelationshipLink } from './relationship-link';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(2),
  backgroundColor: '#ffffff',
  borderRadius: theme.spacing(1),
  boxShadow: '0px -1px 0px 0px #E6E6E6 inset',
}));

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableHead-root': {
    backgroundColor: '#EBEEF2',
    '& .MuiTableCell-root': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  '& .MuiTableBody-root .MuiTableRow-root': {
    backgroundColor: '#ffffff',
    '&:nth-of-type(even)': {
      backgroundColor: '#EBEEF2',
    },
  },
}));

type RelationshipsTableProps = {
  onRemoveClick: (relationship: GetIssueRelationshipsResponseDto) => void;
  relationships: GetIssueRelationshipsResponseDto[];
  readOnly?: boolean;
};

export function RelationshipsTable({
  // eslint-disable-next-line no-unused-vars
  onRemoveClick,
  relationships,
  // eslint-disable-next-line no-unused-vars
  readOnly = false,
}: RelationshipsTableProps): JSX.Element {
  const { t } = useTranslation(['issue']);
  const { getRelationshipTypeLabel } = useLocalizedRelationshipTypes();

  return (
    <StyledTableContainer>
      <StyledTable data-testid='relationships-table'>
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography fontWeight={600}>
                {t('issue:relationships.table.id')}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography fontWeight={600}>
                {t('issue:relationships.table.type')}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography fontWeight={600}>
                {t('issue:relationships.table.name')}
              </Typography>
            </TableCell>
            {/* TODO: Re-enable action column header when delete button is restored */}
            {/* {!readOnly && <TableCell align='right' sx={{ width: '50px' }} />} */}
          </TableRow>
        </TableHead>
        <TableBody>
          {relationships.map((relationship) => (
            <TableRow
              key={relationship.id}
              data-testid={`relationship-row-${relationship.id}`}
            >
              <RelationshipLink relationship={relationship} />
              <TableCell>
                <Typography variant='body2'>
                  {getRelationshipTypeLabel(relationship.relatedObject.type)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant='body2'>
                  {relationship.relatedObject.name}
                </Typography>
              </TableCell>
              {/* eslint-disable-next-line max-len */}
              {/* TODO: Re-enable delete button after every integrated apps updated and use the new POST issues/search endpoint, but without the control-assessment, audit, asset and asset-finding types
              <TableCell align='right'>
                {relationship.relatedObject.type !==
                  ObjectTypes.CONTROL_ASSESSMENT &&
                  !readOnly && (
                    <IconButton
                      onClick={() => {
                        onRemoveClick(relationship);
                      }}
                      aria-label={t('issue:relationships.actions.remove')}
                      data-testid={`remove-relationship-${relationship.id}`}
                    >
                      <RemoveIcon />
                    </IconButton>
                  )}
              </TableCell>
              */}
            </TableRow>
          ))}
        </TableBody>
      </StyledTable>
    </StyledTableContainer>
  );
}
